/* Hallmark · macrostructure: Workbench · tone: operational-editorial · anchor hue: heritage green
 * pre-emit critique: P5 H5 E4 S5 R5 V4 · contrast: pass · nav: existing admin shell · footer: none
 */

import { useState, type ChangeEvent, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import {
  AlertCircle,
  ArrowLeft,
  CalendarDays,
  Check,
  Fingerprint,
  Loader2,
  Save,
  Settings2,
} from 'lucide-react';
import { Button, Input } from '@lms/ui';
import { useCreateProgram } from '../hooks/usePrograms';
import type { ApprovalMode, ProgramStatus } from '@lms/shared/types';
import { copy } from '../copy/copy';
import { Bilingual } from '../components/Bilingual';
import { ProgramCoverField } from '../components/ProgramCoverField';
import { useLanguage } from '../lib/LanguageContext';

function toStartOfDay(value: string): Date {
  return new Date(`${value}T00:00:00`);
}
function toEndOfDay(value: string): Date {
  return new Date(`${value}T23:59:59.999`);
}

interface ProgramFormData {
  approvalMode: ApprovalMode;
  code: string;
  cohort: string;
  coverImage: string;
  description: string;
  endDate: string;
  isRegistrationOpen: boolean;
  generateParticipantNip: boolean;
  name: string;
  registrationClosesAt: string;
  registrationOpensAt: string;
  startDate: string;
  status: ProgramStatus;
}

function FormSection({
  children,
  description,
  id,
  title,
}: {
  children: ReactNode;
  description: ReactNode;
  id: string;
  title: ReactNode;
}) {
  return (
    <section
      id={id}
      className="scroll-mt-6 rounded-3xl bg-surface-container-lowest p-5 shadow-level1 sm:p-6"
    >
      <div className="max-w-2xl">
        <h2 className="font-headline text-xl font-bold tracking-tight text-on-surface">{title}</h2>
        <p className="mt-1 text-sm leading-6 text-on-surface-variant">{description}</p>
      </div>
      <div className="mt-6">{children}</div>
    </section>
  );
}

function Field({
  children,
  helper,
  htmlFor,
  label,
}: {
  children: ReactNode;
  helper?: ReactNode;
  htmlFor: string;
  label: ReactNode;
}) {
  return (
    <div className="space-y-2">
      <label htmlFor={htmlFor} className="block text-sm font-label font-bold text-on-surface">
        {label}
      </label>
      {children}
      {helper && <p className="text-xs leading-5 text-on-surface-variant">{helper}</p>}
    </div>
  );
}

function SummaryItem({ label, value }: { label: ReactNode; value: ReactNode }) {
  return (
    <div className="rounded-2xl bg-surface-container-low p-3.5">
      <dt className="text-[10px] font-label font-bold uppercase tracking-wider text-on-surface-variant">
        {label}
      </dt>
      <dd className="mt-1 min-w-0 overflow-wrap-anywhere text-sm font-semibold text-on-surface">
        {value}
      </dd>
    </div>
  );
}

export function CreateProgram() {
  const navigate = useNavigate();
  const { mutate: create, isPending } = useCreateProgram();
  const { t } = useLanguage();
  const [formError, setFormError] = useState<string | null>(null);
  const [formData, setFormData] = useState<ProgramFormData>({
    approvalMode: 'MANUAL',
    code: '',
    cohort: '',
    coverImage: '',
    description: '',
    endDate: '',
    isRegistrationOpen: false,
    generateParticipantNip: false,
    name: '',
    registrationClosesAt: '',
    registrationOpensAt: '',
    startDate: '',
    status: 'DRAFT',
  });

  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = event.target;
    setFormData(previous => ({ ...previous, [name]: value }));
  };
  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (
      formData.registrationOpensAt &&
      formData.registrationClosesAt &&
      formData.registrationOpensAt > formData.registrationClosesAt
    ) {
      setFormError(t(copy.createProgram.invalidRegistrationSchedule));
      return;
    }
    if (formData.startDate && formData.endDate && formData.startDate > formData.endDate) {
      setFormError(t(copy.createProgram.invalidLearningSchedule));
      return;
    }
    setFormError(null);
    create(
      {
        ...formData,
        cohort: formData.cohort ? Number(formData.cohort) : undefined,
        coverImage: formData.coverImage || undefined,
        registrationOpensAt: formData.registrationOpensAt
          ? toStartOfDay(formData.registrationOpensAt)
          : undefined,
        registrationClosesAt: formData.registrationClosesAt
          ? toEndOfDay(formData.registrationClosesAt)
          : undefined,
        startDate: formData.startDate ? toStartOfDay(formData.startDate) : undefined,
        endDate: formData.endDate ? toEndOfDay(formData.endDate) : undefined,
      },
      { onSuccess: data => void navigate(`/programs/${data.id}`) }
    );
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      <nav
        className="flex items-center gap-2 text-xs text-on-surface-variant"
        aria-label={t(copy.createProgram.breadcrumbPrograms)}
      >
        <Link
          to="/programs"
          className="whitespace-nowrap transition-colors duration-150 hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary/20"
        >
          <Bilingual {...copy.createProgram.breadcrumbPrograms} />
        </Link>
        <span aria-hidden="true">/</span>
        <span className="truncate text-on-surface">
          <Bilingual {...copy.createProgram.breadcrumbCreate} />
        </span>
      </nav>
      <header className="grid gap-5 rounded-[2rem] bg-surface-container-low p-6 sm:p-8 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
        <div className="min-w-0">
          <Link
            to="/programs"
            aria-label={t(copy.createProgram.breadcrumbPrograms)}
            className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-xl text-on-surface-variant transition-colors duration-150 hover:bg-surface-container-high hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary/20 active:scale-95"
          >
            <ArrowLeft size={19} aria-hidden="true" />
          </Link>
          <h1 className="mt-5 overflow-wrap-anywhere font-headline text-3xl font-extrabold tracking-tight text-on-surface">
            <Bilingual {...copy.createProgram.createTitle} />
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-on-surface-variant">
            <Bilingual {...copy.programs.listSubtitle} />
          </p>
        </div>
        <div className="inline-flex items-center gap-2 rounded-2xl bg-surface-container-high px-4 py-3 text-xs font-semibold text-on-surface-variant">
          <Settings2 size={16} className="text-primary" aria-hidden="true" />
          <span>
            <Bilingual {...copy.createProgram.summaryTitle} />
          </span>
        </div>
      </header>
      <nav
        className="flex gap-2 overflow-x-auto rounded-2xl bg-surface-container-low p-2"
        aria-label={t(copy.createProgram.createTitle)}
      >
        <a
          href="#identitas-program"
          className="min-h-10 whitespace-nowrap rounded-xl bg-surface-container-lowest px-3.5 py-2 text-xs font-bold text-primary no-underline shadow-level1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary/20"
        >
          <Bilingual {...copy.createProgram.sectionIdentity} />
        </a>
        <a
          href="#pendaftaran-peserta"
          className="min-h-10 whitespace-nowrap rounded-xl px-3.5 py-2 text-xs font-bold text-on-surface-variant no-underline transition-colors duration-150 hover:bg-surface-container-high hover:text-on-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary/20"
        >
          <Bilingual {...copy.createProgram.sectionAdmission} />
        </a>
        <a
          href="#jadwal-belajar"
          className="min-h-10 whitespace-nowrap rounded-xl px-3.5 py-2 text-xs font-bold text-on-surface-variant no-underline transition-colors duration-150 hover:bg-surface-container-high hover:text-on-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary/20"
        >
          <Bilingual {...copy.createProgram.sectionSchedule} />
        </a>
      </nav>
      <form
        onSubmit={handleSubmit}
        className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_20rem] xl:items-start"
      >
        <div className="space-y-6">
          {formError && (
            <div
              role="alert"
              className="flex items-start gap-3 rounded-2xl bg-error-container p-4 text-sm text-on-error-container"
            >
              <AlertCircle size={18} className="mt-0.5 shrink-0" aria-hidden="true" />
              <span>{formError}</span>
            </div>
          )}
          <FormSection
            id="identitas-program"
            title={<Bilingual {...copy.createProgram.sectionIdentity} />}
            description={<Bilingual {...copy.createProgram.sectionIdentityDesc} />}
          >
            <div className="grid gap-5 md:grid-cols-2">
              <Field
                label={<Bilingual {...copy.createProgram.labelCode} />}
                helper={<Bilingual {...copy.createProgram.descCode} />}
                htmlFor="create-program-code"
              >
                <Input
                  id="create-program-code"
                  name="code"
                  value={formData.code}
                  onChange={handleChange}
                  placeholder={t(copy.createProgram.placeholderCode)}
                  required
                />
              </Field>
              <Field
                label={<Bilingual {...copy.createProgram.labelName} />}
                htmlFor="create-program-name"
              >
                <Input
                  id="create-program-name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={t(copy.createProgram.placeholderName)}
                  required
                />
              </Field>
              <Field
                label={<Bilingual {...copy.createProgram.labelCohort} />}
                helper={<Bilingual {...copy.createProgram.descCohort} />}
                htmlFor="create-program-cohort"
              >
                <Input
                  id="create-program-cohort"
                  type="number"
                  min="1"
                  name="cohort"
                  value={formData.cohort}
                  onChange={handleChange}
                  placeholder={t(copy.createProgram.placeholderCohort)}
                />
              </Field>
            </div>
            <div className="mt-5">
              <Field
                label={<Bilingual {...copy.createProgram.labelDesc} />}
                htmlFor="create-program-description"
              >
                <textarea
                  id="create-program-description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={5}
                  className="w-full rounded-2xl bg-surface-container-low px-4 py-3 text-sm leading-6 text-on-surface outline-none transition-colors duration-150 placeholder:text-on-surface-variant/60 focus:ring-2 focus:ring-primary/20"
                  placeholder={t(copy.createProgram.descPlaceholder)}
                />
              </Field>
            </div>
            <div className="mt-5">
              <ProgramCoverField
                id="program-cover-create"
                value={formData.coverImage}
                programName={formData.name}
                onChange={coverImage => setFormData(previous => ({ ...previous, coverImage }))}
                label={<Bilingual {...copy.createProgram.labelCoverImage} />}
                uploadPrompt={<Bilingual {...copy.createProgram.uploadCoverPrompt} />}
                recommendation={<Bilingual {...copy.createProgram.coverRecommendation} />}
                uploadingLabel={<Bilingual {...copy.createProgram.uploadingCover} />}
                changeLabel={<Bilingual {...copy.createProgram.changeCover} />}
                removeLabel={<Bilingual {...copy.createProgram.removeCover} />}
                previewUnavailable={<Bilingual {...copy.createProgram.coverPreviewUnavailable} />}
              />
            </div>
          </FormSection>
          <FormSection
            id="pendaftaran-peserta"
            title={<Bilingual {...copy.createProgram.sectionAdmission} />}
            description={<Bilingual {...copy.createProgram.sectionAdmissionDesc} />}
          >
            <div className="grid gap-5 md:grid-cols-2">
              <Field
                label={<Bilingual {...copy.createProgram.labelApproval} />}
                htmlFor="create-program-approval"
              >
                <select
                  id="create-program-approval"
                  name="approvalMode"
                  value={formData.approvalMode}
                  onChange={handleChange}
                  className="w-full rounded-xl bg-surface-container-low px-4 py-3 text-sm text-on-surface outline-none transition-colors duration-150 focus:ring-2 focus:ring-primary/20"
                >
                  <option value="MANUAL">{t(copy.createProgram.optionManual)}</option>
                  <option value="AUTO_APPROVE">{t(copy.createProgram.optionAuto)}</option>
                </select>
              </Field>
              <Field
                label={<Bilingual {...copy.createProgram.labelStatus} />}
                htmlFor="create-program-status"
              >
                <select
                  id="create-program-status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full rounded-xl bg-surface-container-low px-4 py-3 text-sm text-on-surface outline-none transition-colors duration-150 focus:ring-2 focus:ring-primary/20"
                >
                  <option value="DRAFT">{t(copy.createProgram.optionDraft)}</option>
                  <option value="PUBLISHED">{t(copy.createProgram.optionPublished)}</option>
                </select>
              </Field>
            </div>
            <div className="mt-5 flex flex-col gap-4 rounded-2xl bg-surface-container-low p-5 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="font-semibold text-on-surface">
                  <Bilingual {...copy.createProgram.labelRegActivation} />
                </p>
                <p className="mt-1 text-xs leading-5 text-on-surface-variant">
                  <Bilingual {...copy.createProgram.descRegActivation} />
                </p>
              </div>
              <button
                type="button"
                onClick={() =>
                  setFormData(previous => ({
                    ...previous,
                    isRegistrationOpen: !previous.isRegistrationOpen,
                  }))
                }
                aria-label={t(copy.createProgram.labelRegActivation)}
                aria-pressed={formData.isRegistrationOpen}
                className={`relative h-9 w-16 shrink-0 rounded-full transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary/20 active:scale-95 ${formData.isRegistrationOpen ? 'bg-primary' : 'bg-surface-container-highest'}`}
              >
                <span
                  className={`absolute left-1 top-1 h-7 w-7 rounded-full bg-surface-container-lowest shadow-level1 transition-transform duration-150 ${formData.isRegistrationOpen ? 'translate-x-7' : 'translate-x-0'}`}
                />
              </button>
            </div>
            <div className="mt-5 flex flex-col gap-4 rounded-2xl bg-secondary-container/45 p-5 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 gap-3">
                <Fingerprint
                  className="mt-0.5 shrink-0 text-primary"
                  size={20}
                  aria-hidden="true"
                />
                <div>
                  <p className="font-semibold text-on-surface">
                    <Bilingual {...copy.createProgram.labelAutomaticNip} />
                  </p>
                  <p className="mt-1 max-w-2xl text-xs leading-5 text-on-surface-variant">
                    <Bilingual {...copy.createProgram.descAutomaticNip} />
                  </p>
                  <p className="mt-2 font-mono text-[11px] font-bold text-primary">
                    <Bilingual {...copy.createProgram.nipFormatHelp} />
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() =>
                  setFormData(previous => ({
                    ...previous,
                    generateParticipantNip: !previous.generateParticipantNip,
                  }))
                }
                aria-label={t(copy.createProgram.labelAutomaticNip)}
                aria-pressed={formData.generateParticipantNip}
                className={`relative h-9 w-16 shrink-0 rounded-full transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary/20 active:scale-95 ${formData.generateParticipantNip ? 'bg-primary' : 'bg-surface-container-highest'}`}
              >
                <span
                  className={`absolute left-1 top-1 h-7 w-7 rounded-full bg-surface-container-lowest shadow-level1 transition-transform duration-150 ${formData.generateParticipantNip ? 'translate-x-7' : 'translate-x-0'}`}
                />
              </button>
            </div>
            <div className="mt-5 grid gap-5 md:grid-cols-2">
              <Field
                label={<Bilingual {...copy.createProgram.labelRegistrationOpenDate} />}
                htmlFor="create-program-registration-opens-at"
              >
                <Input
                  id="create-program-registration-opens-at"
                  type="date"
                  name="registrationOpensAt"
                  value={formData.registrationOpensAt}
                  onChange={handleChange}
                  disabled={!formData.isRegistrationOpen}
                />
              </Field>
              <Field
                label={<Bilingual {...copy.createProgram.labelRegistrationCloseDate} />}
                htmlFor="create-program-registration-closes-at"
              >
                <Input
                  id="create-program-registration-closes-at"
                  type="date"
                  name="registrationClosesAt"
                  min={formData.registrationOpensAt || undefined}
                  value={formData.registrationClosesAt}
                  onChange={handleChange}
                  disabled={!formData.isRegistrationOpen}
                />
              </Field>
            </div>
          </FormSection>
          <FormSection
            id="jadwal-belajar"
            title={<Bilingual {...copy.createProgram.sectionSchedule} />}
            description={<Bilingual {...copy.createProgram.sectionScheduleDesc} />}
          >
            <div className="grid gap-5 md:grid-cols-2">
              <Field
                label={<Bilingual {...copy.createProgram.labelStartDate} />}
                htmlFor="create-program-start-date"
              >
                <Input
                  id="create-program-start-date"
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                />
              </Field>
              <Field
                label={<Bilingual {...copy.createProgram.labelEndDate} />}
                htmlFor="create-program-end-date"
              >
                <Input
                  id="create-program-end-date"
                  type="date"
                  name="endDate"
                  min={formData.startDate || undefined}
                  value={formData.endDate}
                  onChange={handleChange}
                />
              </Field>
            </div>
          </FormSection>
          <div className="flex flex-col-reverse gap-3 rounded-3xl bg-surface-container-low p-4 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="secondary"
              onClick={() => void navigate('/programs')}
              disabled={isPending}
              className="min-h-11 whitespace-nowrap"
            >
              <Bilingual {...copy.common.cancel} />
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isPending}
              className="min-h-11 whitespace-nowrap px-6"
            >
              {isPending ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
              <Bilingual {...copy.programs.createBtn} />
            </Button>
          </div>
        </div>
        <aside className="rounded-3xl bg-surface-container-low p-5 xl:sticky xl:top-6">
          <div className="flex items-center gap-2 text-primary">
            <Check size={17} aria-hidden="true" />
            <h2 className="font-headline text-base font-bold text-on-surface">
              <Bilingual {...copy.createProgram.summaryTitle} />
            </h2>
          </div>
          <dl className="mt-5 space-y-3">
            <SummaryItem
              label={<Bilingual {...copy.createProgram.labelName} />}
              value={formData.name || t(copy.createProgram.summaryEmpty)}
            />
            <SummaryItem
              label={<Bilingual {...copy.createProgram.labelCode} />}
              value={formData.code || t(copy.createProgram.summaryEmpty)}
            />
            <SummaryItem
              label={<Bilingual {...copy.createProgram.labelCohort} />}
              value={formData.cohort || t(copy.createProgram.summaryEmpty)}
            />
            <SummaryItem
              label={<Bilingual {...copy.createProgram.labelStatus} />}
              value={
                formData.status === 'PUBLISHED'
                  ? t(copy.createProgram.optionPublished)
                  : t(copy.createProgram.optionDraft)
              }
            />
            <SummaryItem
              label={<Bilingual {...copy.createProgram.labelRegActivation} />}
              value={
                formData.isRegistrationOpen
                  ? t(copy.createProgram.summaryRegistrationOpen)
                  : t(copy.createProgram.summaryRegistrationClosed)
              }
            />
            <SummaryItem
              label={<Bilingual {...copy.createProgram.labelAutomaticNip} />}
              value={
                formData.generateParticipantNip
                  ? t(copy.createProgram.summaryNipAutomatic)
                  : t(copy.createProgram.summaryNipDisabled)
              }
            />
          </dl>
          <div className="mt-5 rounded-2xl bg-surface-container-lowest p-3.5 text-xs leading-5 text-on-surface-variant">
            <CalendarDays size={15} className="mb-2 text-primary" aria-hidden="true" />
            <Bilingual {...copy.createProgram.sectionScheduleDesc} />
          </div>
        </aside>
      </form>
    </div>
  );
}
